import React from "react"
import { Modal } from "antd"
import { useNavigate } from "react-router-dom"

import { Loader } from "shared/ui/Loader/Loader.component"
import { ACCENT } from "constants/brand"
import {
  RunExclusive,
  useQuestionnaireMetadataQuery,
  useSessionLineageQuery,
} from "../api/useLineage"
import { provenanceLink } from "../utils/provenanceLinks"
import { LazyLineageView } from "./LazyLineageView.component"
import type { ScriptLabelOptions } from "../utils/traceTable"

interface LineageModalProps {
  open: boolean
  onClose: () => void
  /** The live reporting query instance whose transforms we trace. */
  queryInstance: string | null
  /** The open project: its questionnaire shows beside a collected variable, its Provenance tab is linked. */
  projectId: string
  /** A variable or table of the graph to open on */
  focusId?: string | null
  /** What the names a feature's own scripts make stand for, shown in place of them */
  describeHelper?: (name: string) => string | undefined
  /** What the view that opened the modal knows about its own scripts, to name them in the script log */
  scriptLabels?: ScriptLabelOptions
  /** Runs the read of the lineage in its turn with the session's other calls (see `RunExclusive`) */
  runExclusive?: RunExclusive
}

/**
 * Variable-lineage modal: fetches the derivation graph of the current reporting session and
 * renders it as an interactive graph (click a variable to trace what it derives from and what it
 * affects). A collected variable also shows its questionnaire definition and any differences
 * between the questionnaire and the data, fetched alongside.
 */
export const LineageModal: React.FC<LineageModalProps> = ({
  open,
  onClose,
  queryInstance,
  projectId,
  focusId,
  describeHelper,
  scriptLabels,
  runExclusive,
}) => {
  const navigate = useNavigate()
  // Read again each time the modal opens: the session changes with every command.
  const lineage = useSessionLineageQuery(queryInstance, open, runExclusive)
  // Design provenance is advisory: leaves without it simply show no design detail.
  const qnrMeta = useQuestionnaireMetadataQuery(projectId, open)

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width="92vw"
      style={{ top: 24 }}
      styles={{ body: { height: "82vh", padding: 0 } }}
      title={
        <span className="flex items-center gap-3">
          Variable lineage
          <button
            type="button"
            onClick={() => {
              onClose()
              navigate(provenanceLink(projectId, "lineage"))
            }}
            className="text-[12px] font-normal text-slate-500 underline hover:text-slate-700"
          >
            Open in Provenance
          </button>
        </span>
      }
      destroyOnClose
    >
      <div className="h-full w-full">
        {lineage.isFetching || (!lineage.data && !lineage.isError) ? (
          <div className="flex h-full items-center justify-center">
            <Loader fontSize={40} color={ACCENT} />
          </div>
        ) : lineage.isError ? (
          <div className="flex h-full items-center justify-center text-[13px] text-slate-500">
            Couldn’t load lineage for this session.
          </div>
        ) : lineage.data ? (
          <LazyLineageView dag={lineage.data} qnrMeta={qnrMeta.data ?? null} focusId={focusId}
            describeHelper={describeHelper}
            scriptLabels={scriptLabels}
          />
        ) : null}
      </div>
    </Modal>
  )
}

export default LineageModal
